import React, { useState } from "react";
import { toast } from "sonner";
import { useNavigate, Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const OnboardingPage = () => {
  const { user, loading, setUser } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    first_name: user?.first_name || "",
    age: user?.age || "",
    occupation: user?.occupation || "",
    gender: user?.gender || "",
  });
  const [saving, setSaving] = useState(false);

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.age || !form.occupation || !form.gender) {
      toast.error("Please fill in all fields");
      return;
    }
    setSaving(true);
    try {
      const res = await api.post("/profile", {
        first_name: form.first_name.trim(),
        age: parseInt(form.age, 10),
        occupation: form.occupation.trim(),
        gender: form.gender,
      });
      setUser(res.data);
      toast.success("Profile saved");
      nav(res.data.is_admin ? "/admin" : "/attendees");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen px-5 py-10 max-w-md mx-auto mm-fade-in">
      <div className="overline mb-2" data-testid="brand-name">Muslim Mingle</div>
      <h1 className="font-serif-display text-4xl mb-1">Your profile</h1>
      <div className="mm-divider-gold" />
      <p className="text-sm text-slate-600 mb-10">Fellow attendees will see your name, age and occupation.</p>

      <form onSubmit={onSubmit} className="space-y-8">
        <div>
          <label className="overline block mb-2">First Name</label>
          <input
            data-testid="onboarding-first-name"
            className="mm-input"
            value={form.first_name}
            onChange={(e) => set("first_name", e.target.value)}
            placeholder="Aisha"
            maxLength={40}
            required
          />
        </div>
        <div>
          <label className="overline block mb-2">Age</label>
          <input
            data-testid="onboarding-age"
            className="mm-input"
            type="number"
            min={18}
            max={99}
            value={form.age}
            onChange={(e) => set("age", e.target.value)}
            placeholder="28"
            required
          />
        </div>
        <div>
          <label className="overline block mb-2">Occupation</label>
          <input
            data-testid="onboarding-occupation"
            className="mm-input"
            value={form.occupation}
            onChange={(e) => set("occupation", e.target.value)}
            placeholder="Software Engineer"
            maxLength={80}
            required
          />
        </div>
        <div>
          <label className="overline block mb-3">Gender</label>
          <div className="grid grid-cols-2 gap-3">
            {["male", "female"].map((g) => (
              <button
                key={g}
                type="button"
                data-testid={`onboarding-gender-${g}`}
                onClick={() => set("gender", g)}
                className={
                  form.gender === g
                    ? "mm-btn-primary"
                    : "mm-btn-outline"
                }
                style={{ textTransform: "capitalize" }}
              >
                {g}
              </button>
            ))}
          </div>
        </div>
        <button
          type="submit"
          data-testid="onboarding-submit"
          className="mm-btn-primary"
          disabled={saving}
        >
          {saving ? "Saving…" : "Enter event"}
        </button>
      </form>
    </div>
  );
};

export default OnboardingPage;
